"use client"

import type React from "react"
import { useEffect, useState, useCallback } from "react"
import { Edit, Trash2, Search, X, Printer } from "lucide-react"
import type { ModuleConfig } from "../../config/products/types"
import { useApi } from "../../hooks/Apis"
import ConfirmationDialog from "../../hooks/ConfirmationDialog"
import Pagination from "./pagination"
import Loader from "../../hooks/Loader"
import { useSubmissionContext } from "./context"
import dayjs from "dayjs"
import utils from "../../utils"
import { useSnackbar } from "notistack"
import ReceiptModal from "../layout/ReceiptModal"

interface GenericTableProps {
  config: ModuleConfig
  onEdit: (item: any) => void
  params?: Record<string, any>
  id?: number
  hideActionMenu?: boolean
  refreshCount?: number
  dateRange?: { startDate: string; endDate: string }
}

const Table: React.FC<GenericTableProps> = ({ config, onEdit, params, hideActionMenu, refreshCount, dateRange, ...rest }) => {
  const key = utils.getKeyField(config)
  const keyField = `${key}id`
  const localKey = `${key}s`
  const { apiRequest } = useApi()
  const [data, setData] = useState<any[]>([])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const { submissionState } = useSubmissionContext()
  const [searchTerm, setSearchTerm] = useState<string>("")
  const updateLocal = config?.updateLocal
  const clientorganizationid = localStorage.getItem("clientorganizationid") || ""
  const { enqueueSnackbar } = useSnackbar()
  const clientUser = localStorage.getItem("clientuser")  ? JSON.parse(localStorage.getItem("clientuser") || "{}") : null
  const appConfig = clientUser?.clientData?.appconfig || {}
  const hideProfit = appConfig.hideProfit ?? false
  const [showReceiptModal, setShowReceiptModal] = useState(false)
  const [selectedReceiptItem, setSelectedReceiptItem] = useState<any>(null)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [showBulkReceiptModal, setShowBulkReceiptModal] = useState(false)
  const isAllSelected = data.length > 0 && selectedRows.size === data.length

  const toggleRowSelection = (id: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(data.map((item) => item[keyField] || item.id)))
    }
  }

  const selectedItems = data.filter((item) =>
    selectedRows.has(item[keyField] || item.id)
  )

  const handleViewReceipt = (item: any) => {
    setSelectedReceiptItem(item)
    setShowReceiptModal(true)
  }

  const handleSearch = (value: string) => {
    setCurrentPage(1)
    fetchData(value)
  }

  const handleClearSearch = async () => {
    setSearchTerm("")
    setCurrentPage(1)
  }

  const fetchData = useCallback(
    async (searchValue?: string) => {
      setLoading(true)
      const { url = "", payload = {} } = config.apiEndpoints.list || {}
      const additionalParams = payload.hideProject ? {} : { projectid: (rest as any)?.id }
      const clientPayload = { clientorganizationid: clientorganizationid ?? 1 }
      const tempPayload = {
        ...payload,
        ...params,
        page: currentPage,
        pageSize: itemsPerPage,
        searchTerm: searchValue ?? searchTerm,
        ...(dateRange
          ? { startDate: dateRange.startDate, endDate: dateRange.endDate }
          : {}),
        ...additionalParams,
        ...clientPayload,
      }
      const response = await apiRequest({ method: "POST", url, data: tempPayload })
      setData(response?.data || [])
      setTotalItems(response?.totalItems || 0)
      setSelectedRows(new Set())
      const custom = localKey.toLowerCase() === "products"
      if (updateLocal && !custom) {
        localStorage.setItem(localKey, JSON.stringify(response?.data))
      }
      setLoading(false)
    },
    [config, currentPage, searchTerm, dateRange, params, clientorganizationid]
  )

  const confirmDeleteExpense = async () => {
    setLoading(true)
    const { payload } = config.apiEndpoints.delete
    const tempParams = { ...payload }
    if (deleteId !== null) {
      const deleteData = { ...tempParams, [keyField]: deleteId }
      await apiRequest({
        method: "POST",
        url: `${config.apiEndpoints.delete.url}/${deleteId}`,
        data: deleteData,
      })
      fetchData()
      setShowDeleteDialog(false)
      setDeleteId(null)
      setLoading(false)
    }
  }

  const handleDeleteClick = (id: number) => {
    setDeleteId(id)
    setShowDeleteDialog(true)
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  const fetchAllProductsData = async () => {
    try {
      setLoading(true)
      const url = `${utils.baseUrl}/api/products/all`
      const response = await apiRequest({ method: "GET", url })
      localStorage.setItem("products", JSON.stringify(response?.data))
      setLoading(false)
    } catch (error) {
      console.error("Error in fetchProductsData:", error)
      enqueueSnackbar("Products Data Loading Failed. Please try again.", {
        variant: "error",
      })
      setLoading(false)
    }
  }

  useEffect(() => {
    if (config.addDateFilter && !dateRange) return
    fetchData()
    fetchAllProductsData()
  }, [config, currentPage, submissionState, refreshCount, searchTerm, dateRange?.startDate, dateRange?.endDate])

  useEffect(() => {
    setCurrentPage(1)
    setSearchTerm("")
  }, [config])

  const renderCellContent = (field: any, item: any) => {
    if (field.type === "date" && item[field.name]) {
      return dayjs(item[field.name]).format(utils.dateFormat)
    } else if (field.type === "json" && item[field.name]) {
      return (
        <pre className="max-w-xs overflow-hidden text-ellipsis whitespace-pre-wrap rounded bg-gray-100 p-2 text-xs dark:bg-gray-700">
          {JSON.stringify(item[field.name], null, 2)}
        </pre>
      )
    } else if (field.convertValue) {
      return field.convertValue(item[field.name])
    } else if (field.render) {
      return field.render(item[field.name], item)
    } else {
      return item[field.name]
    }
  }

  const filteredFields = config.fields.filter((field) => {
    if (field.name === "profit") return !hideProfit
    return !field?.hide
  })

  const ActionButton = ({
    onClick,
    icon,
    variant = "ghost",
  }: {
    onClick: () => void
    icon: React.ReactNode
    variant?: "ghost" | "danger"
  }) => {
    const variants = {
      ghost:
        "text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700",
      danger:
        "text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-300 dark:hover:bg-red-900/20",
    }
    return (
      <button
        onClick={onClick}
        className={`rounded-lg p-2 transition-colors duration-200 ${variants[variant]}`}
      >
        {icon}
      </button>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      {config?.addSearch && (
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${config?.title}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch(searchTerm)}
              className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-10 py-2.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:border-purple-400"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            onClick={() => handleSearch(searchTerm)}
            className="rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Search
          </button>
        </div>
      )}

      {/* Bulk Action Bar */}
      {config.showReceipt && selectedRows.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 dark:border-purple-800 dark:bg-purple-900/20">
          <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
            {selectedRows.size} item{selectedRows.size > 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedRows(new Set())}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-100 dark:text-purple-400 dark:hover:bg-purple-800/30 transition"
            >
              Clear
            </button>
            <button
              onClick={() => setShowBulkReceiptModal(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700 transition"
            >
              <Printer size={14} />
              Print Receipt ({selectedRows.size})
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <div className="p-8">
            <Loader />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {/* Checkbox column — only on receipt-enabled screens */}
                  {config.showReceipt && (
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      />
                    </th>
                  )}
                  {filteredFields.map((field) => (
                    <th
                      key={field.name}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                      style={{ minWidth: field.width || "150px" }}
                    >
                      {field.label}
                    </th>
                  ))}
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        filteredFields.length + 1 + (config.showReceipt ? 1 : 0)
                      }
                      className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      No data available
                    </td>
                  </tr>
                ) : (
                  data.map((item: any) => {
                    const itemId = item[keyField] || item.id
                    const isSelected = selectedRows.has(itemId)
                    return (
                      <tr
                        key={itemId}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 ${
                          isSelected
                            ? "bg-purple-50 dark:bg-purple-900/10"
                            : ""
                        }`}
                      >
                        {/* Row checkbox */}
                        {config.showReceipt && (
                          <td className="w-10 px-4 py-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRowSelection(itemId)}
                              className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                            />
                          </td>
                        )}
                        {filteredFields.map((field) => (
                          <td
                            key={field.name}
                            className={`px-6 py-4 text-sm text-gray-900 dark:text-gray-100 ${
                              field.getCustomClass ? field.getCustomClass(item) : ""
                            }`}
                          >
                            {renderCellContent(field, item)}
                          </td>
                        ))}
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-1">
                            <ActionButton
                              onClick={() => onEdit(item)}
                              icon={<Edit size={16} />}
                              variant="ghost"
                            />
                            {config.showReceipt && (
                              <ActionButton
                                onClick={() => handleViewReceipt(item)}
                                icon={<Printer size={16} />}
                                variant="ghost"
                              />
                            )}
                            <ActionButton
                              onClick={() =>
                                handleDeleteClick(item[keyField] || 0)
                              }
                              icon={<Trash2 size={16} />}
                              variant="danger"
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
      />

      {/* Delete Confirmation */}
      {showDeleteDialog && (
        <ConfirmationDialog
          open={showDeleteDialog}
          title="Confirm Deletion"
          content={`Are you sure you want to delete this ${config.title}? This action cannot be undone.`}
          onCancel={() => setShowDeleteDialog(false)}
          onConfirm={confirmDeleteExpense}
          confirmDiscard="Delete"
        />
      )}

      {/* Single Receipt Modal */}
      {showReceiptModal && selectedReceiptItem && (
        <ReceiptModal
          item={selectedReceiptItem}
          onClose={() => {
            setShowReceiptModal(false)
            setSelectedReceiptItem(null)
          }}
        />
      )}

      {/* Bulk Receipt Modal */}
      {showBulkReceiptModal && selectedItems.length > 0 && (
        <ReceiptModal
          item={selectedItems[0]}
          items={selectedItems}
          onClose={() => setShowBulkReceiptModal(false)}
        />
      )}
    </div>
  )
}

export default Table